import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api, { API_BASE } from '../lib/api';
import { SeverityBadge } from '../components/SeverityBadge';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, ShieldCheck, Plus, Edit3, Trash2, Globe, Download, Activity, FileX2, Mail, CheckCircle2, Send, Unplug, CreditCard, XCircle } from 'lucide-react';

// Compute total active credits from a user's `credits` array (mirrors backend `active_credits`).
const computeActiveCredits = (creditsArr) => {
  const now = new Date().toISOString();
  return (creditsArr || []).reduce((sum, c) => {
    if ((c.remaining || 0) <= 0) return sum;
    if (c.expires_at && c.expires_at <= now) return sum;
    return sum + (c.remaining || 0);
  }, 0);
};

const KPI = ({ label, value, accent }) => (
  <div className="bg-background p-6" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="label-mono mb-2">{label}</div>
    <div className={`font-mono text-4xl ${accent ? 'text-gradient-pt' : ''}`}>{value ?? '—'}</div>
  </div>
);

const Sparkline = ({ data }) => {
  if (!data || data.length === 0) return <div className="h-16 text-xs text-muted-foreground">No data</div>;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d) => (
        <div key={d.day} title={`${d.day}: ${d.count}`} className="flex-1 gradient-pt" style={{ height: `${(d.count / max) * 100}%`, minHeight: '4px' }} />
      ))}
    </div>
  );
};

export default function Admin() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'rules';
  const [v2, setV2] = useState(null);
  const [legacyAnalytics, setLegacyAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [rules, setRules] = useState([]);
  const [news, setNews] = useState([]);
  const [emails, setEmails] = useState([]);
  const [gmail, setGmail] = useState({ connected: false, configured: false });
  const [gmailConnections, setGmailConnections] = useState([]);
  const [testEmail, setTestEmail] = useState('');

  // Modal state
  const [editingRule, setEditingRule] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [billingUser, setBillingUser] = useState(null);
  const [ingestUrl, setIngestUrl] = useState('');

  // Regulation rules pagination + search
  const [rulesPage, setRulesPage] = useState(1);
  const [rulesSearch, setRulesSearch] = useState('');
  const RULES_PER_PAGE = 10;

  const filteredRules = React.useMemo(() => {
    const q = rulesSearch.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((r) =>
      (r.check_id || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q) ||
      (r.severity || '').toLowerCase().includes(q) ||
      (r.what_to_detect || '').toLowerCase().includes(q)
    );
  }, [rules, rulesSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredRules.length / RULES_PER_PAGE));
  React.useEffect(() => { if (rulesPage > totalPages) setRulesPage(1); }, [rulesPage, totalPages]);
  const pagedRules = filteredRules.slice((rulesPage - 1) * RULES_PER_PAGE, rulesPage * RULES_PER_PAGE);

  const deleteRule = async (check_id) => {
    if (!window.confirm(`Permanently delete rule ${check_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/regulations/${check_id}`);
      toast.success(`Rule ${check_id} deleted`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete rule');
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [a2, a, u, ad, r, n, e, gm, gc] = await Promise.all([
        api.get('/admin/analytics-v2'),
        api.get('/admin/analytics'),
        api.get('/admin/users'),
        api.get('/admin/audits'),
        api.get('/regulations'),
        api.get('/admin/news'),
        api.get('/admin/email-log'),
        api.get('/account/gmail/status'),
        api.get('/admin/gmail/connections'),
      ]);
      setV2(a2.data);
      setLegacyAnalytics(a.data);
      setUsers(u.data.items || []);
      setAudits(ad.data.items || []);
      setRules(r.data.rules || []);
      setNews(n.data.items || []);
      setEmails(e.data.items || []);
      setGmail(gm.data || { connected: false, configured: false });
      setGmailConnections(gc.data.items || []);
    } catch (err) {
      toast.error('Could not load admin data');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live users refresh every 30s
  useEffect(() => {
    const t = setInterval(() => {
      api.get('/admin/analytics-v2').then(({ data }) => setV2(data)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const toggleRule = async (id) => {
    await api.patch(`/regulations/${id}/toggle`);
    loadAll();
  };

  const triggerCrawl = async () => {
    try {
      const { data } = await api.post('/news/crawl');
      const per = Array.isArray(data.per_source) ? data.per_source : [];
      const ok = per.filter(s => s.status === 'success').length;
      const failed = per.filter(s => s.status === 'error');
      const zero = per.filter(s => s.status === 'zero_items');
      const total = per.length;
      const parts = [`Crawl ${data.status}`,
                     `${ok}/${total} sources OK`,
                     `${data.new_items_found} new items`];
      if (failed.length) parts.push(`${failed.length} error${failed.length === 1 ? '' : 's'}`);
      if (zero.length) parts.push(`${zero.length} zero-item`);
      const msg = parts.join(' · ');
      if (data.status === 'success') toast.success(msg);
      else if (data.status === 'degraded') toast.warning(msg);
      else toast.error(msg);
      loadAll();
    } catch {
      toast.error('Crawl request failed');
    }
  };

  const ingest = async () => {
    if (!ingestUrl) { toast.error('Paste a URL first'); return; }
    try {
      await api.post('/admin/ingest-url', { url: ingestUrl });
      toast.success('Page ingested as news item');
      setIngestUrl('');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ingest failed');
    }
  };

  const downloadPdf = async (audit_id) => {
    try {
      const token = localStorage.getItem('pc_session_token') || '';
      const url = `${API_BASE}/audit/${audit_id}/report/pdf`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PackCheck-${audit_id}.pdf`;
      a.click();
    } catch (e) {
      toast.error('Could not download report (audit may not be complete)');
    }
  };

  const connectGmail = async () => {
    try {
      const { data } = await api.get('/account/gmail/connect-url', { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Gmail connect');
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect your Gmail? You will not be able to send reports until reconnected.')) return;
    try {
      await api.delete('/account/gmail/disconnect');
      toast.success('Gmail disconnected');
      loadAll();
    } catch {
      toast.error('Could not disconnect');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email to send to'); return; }
    try {
      const { data } = await api.post('/account/gmail/test-send', { to_email: testEmail });
      if (data.status === 'sent') toast.success(`Test email sent to ${testEmail}`);
      else toast.error(data.error || `Send returned: ${data.status}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    }
  };

  if (!v2 || !legacyAnalytics) {
    return <Layout><div className="p-10"><div className="dot-loader"><span/><span/><span/></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-12">
        {/* HEADER */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono flex items-center gap-2"><ShieldCheck size={12} /> Admin / Console</div>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2">PackCheck management</h1>
            <p className="text-sm text-muted-foreground mt-2">
              <span className="inline-flex items-center gap-1.5"><Activity size={12} className="text-accent" /> {v2.live_users} live in last 5 min</span>
              <span className="mx-3 text-border">·</span>
              {v2.total_users} users · {v2.total_audits} audits
            </p>
          </div>
          <Button onClick={triggerCrawl} variant="outline" className="rounded-none" data-testid="admin-crawl-btn">
            <RefreshCw size={14} strokeWidth={1.5} /> Run crawl
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border mb-10" data-testid="admin-kpis">
          <KPI label="Total audits" value={v2.total_audits} />
          <KPI label="Completed" value={v2.completed_audits} />
          <KPI label="In progress" value={v2.in_progress_audits} accent={v2.in_progress_audits > 0} />
          <KPI label="Drafts" value={v2.draft_audits} />
          <KPI label="Abandoned" value={v2.abandoned_audits} />
          <KPI label="Live users" value={v2.live_users} accent={v2.live_users > 0} />
        </div>

        {/* CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border mb-10">
          <div className="bg-background p-6" data-testid="chart-audits-per-day">
            <div className="label-mono mb-3">Audits — last 30 days</div>
            <Sparkline data={v2.audits_per_day} />
            <div className="mt-3 font-mono text-[10px] text-muted-foreground flex justify-between">
              <span>{v2.audits_per_day?.[0]?.day || '—'}</span>
              <span>{v2.audits_per_day?.[v2.audits_per_day.length - 1]?.day || '—'}</span>
            </div>
          </div>
          <div className="bg-background p-6" data-testid="top-flags">
            <div className="label-mono mb-3">Top flagged checks</div>
            <div className="space-y-2 text-sm">
              {(legacyAnalytics.top_flags || []).slice(0, 6).map((f) => (
                <div key={f.check_id} className="flex justify-between font-mono text-xs">
                  <span>{f.check_id}</span><span>{f.count}</span>
                </div>
              ))}
              {(!legacyAnalytics.top_flags || legacyAnalytics.top_flags.length === 0) && (
                <div className="text-xs text-muted-foreground">No flags yet.</div>
              )}
            </div>
          </div>
          <div className="bg-background p-6" data-testid="top-users">
            <div className="label-mono mb-3">Top users by audits</div>
            <div className="space-y-2 text-sm">
              {(v2.top_users || []).map((u) => (
                <div key={u.email} className="flex justify-between text-xs">
                  <span className="truncate">{u.name || u.email}</span><span className="font-mono">{u.count}</span>
                </div>
              ))}
              {(!v2.top_users || v2.top_users.length === 0) && (
                <div className="text-xs text-muted-foreground">No audits yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* MAIN TABS */}
        <Tabs defaultValue={initialTab}>
          <TabsList className="rounded-none border border-border bg-transparent p-0 h-auto flex-wrap">
            {[
              ['rules', `Regulation rules (${rules.length})`],
              ['coupons', 'Coupons'],
              ['news', `News (${news.length})`],
              ['ingest', `Ingest URL`],
              ['crawls', `Crawler runs`],
              ['audits', `Audits (${audits.length})`],
              ['users', `Users (${users.length})`],
              ['email', `Email`],
              ['emails', `Email log (${emails.length})`],
            ].map(([v, l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none border-r border-border last:border-r-0 px-5 py-3 text-sm data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>

          {/* RULES */}
          <TabsContent value="rules" className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-mono">{pagedRules.length}</span> of <span className="font-mono">{filteredRules.length}</span>
                {rulesSearch && rules.length !== filteredRules.length && (
                  <> (filtered from {rules.length})</>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={rulesSearch}
                  onChange={(e) => { setRulesSearch(e.target.value); setRulesPage(1); }}
                  placeholder="Search by ID, category, severity, text…"
                  className="rounded-none h-10 w-full sm:w-72"
                  data-testid="rules-search"
                />
                <Button onClick={() => setEditingRule({ __new: true, check_id: '', category: '', check_type: 'text', severity: 'MEDIUM', what_to_detect: '', red_flag_condition: '', improvement_suggestion: '', regulation_reference: '', is_active: true })} className="rounded-none whitespace-nowrap" data-testid="add-rule-btn">
                  <Plus size={14} strokeWidth={1.5} /> New rule
                </Button>
              </div>
            </div>
            <div className="border border-border scroll-table" data-testid="admin-rules-table">
              <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                <div className="col-span-2">Check ID</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Severity</div>
                <div className="col-span-4">What to detect</div>
                <div className="col-span-1">Active</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {pagedRules.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {rulesSearch ? `No rules match "${rulesSearch}"` : 'No rules yet.'}
                </div>
              )}
              {pagedRules.map((r) => (
                <div key={r.check_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center">
                  <div className="col-span-2 font-mono">{r.check_id}</div>
                  <div className="col-span-2 text-xs">{r.category}</div>
                  <div className="col-span-1"><SeverityBadge severity={r.severity} /></div>
                  <div className="col-span-4 text-sm line-clamp-2">{r.what_to_detect}</div>
                  <div className="col-span-1 font-mono text-xs">{r.is_active ? 'YES' : 'NO'}</div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button onClick={() => setEditingRule(r)} title="Edit" className="w-8 h-8 grid place-items-center border border-border hover:bg-surface-hover" data-testid={`edit-rule-${r.check_id}`}>
                      <Edit3 size={13} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => toggleRule(r.check_id)} title={r.is_active ? 'Deactivate' : 'Activate'} className="text-xs underline text-muted-foreground hover:text-foreground px-2" data-testid={`toggle-rule-${r.check_id}`}>Toggle</button>
                    <button onClick={() => deleteRule(r.check_id)} title="Delete" className="w-8 h-8 grid place-items-center border border-border hover:bg-severity-high hover:text-background" data-testid={`delete-rule-${r.check_id}`}>
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm" data-testid="rules-pagination">
                <div className="text-muted-foreground">
                  Page <span className="font-mono">{rulesPage}</span> of <span className="font-mono">{totalPages}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setRulesPage(1)}
                    disabled={rulesPage === 1}
                    className="h-9 px-3 border border-border hover:bg-surface-hover disabled:opacity-40 font-mono text-xs"
                    data-testid="rules-page-first"
                  >« First</button>
                  <button
                    onClick={() => setRulesPage((p) => Math.max(1, p - 1))}
                    disabled={rulesPage === 1}
                    className="h-9 px-3 border border-border hover:bg-surface-hover disabled:opacity-40 font-mono text-xs"
                    data-testid="rules-page-prev"
                  >‹ Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => Math.abs(p - rulesPage) <= 2 || p === 1 || p === totalPages)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '…' ? (
                        <span key={`gap-${idx}`} className="px-1 text-muted-foreground">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setRulesPage(p)}
                          className={`h-9 w-9 grid place-items-center border border-border font-mono text-xs ${p === rulesPage ? 'bg-foreground text-background' : 'hover:bg-surface-hover'}`}
                          data-testid={`rules-page-${p}`}
                        >{p}</button>
                      )
                    )}
                  <button
                    onClick={() => setRulesPage((p) => Math.min(totalPages, p + 1))}
                    disabled={rulesPage === totalPages}
                    className="h-9 px-3 border border-border hover:bg-surface-hover disabled:opacity-40 font-mono text-xs"
                    data-testid="rules-page-next"
                  >Next ›</button>
                  <button
                    onClick={() => setRulesPage(totalPages)}
                    disabled={rulesPage === totalPages}
                    className="h-9 px-3 border border-border hover:bg-surface-hover disabled:opacity-40 font-mono text-xs"
                    data-testid="rules-page-last"
                  >Last »</button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* COUPONS */}
          <TabsContent value="coupons" className="mt-6" data-testid="coupons-tab">
            <CouponsTab />
          </TabsContent>

          {/* NEWS */}
          <TabsContent value="news" className="mt-6">
            <div className="flex justify-end mb-3">
              <Button onClick={() => setEditingNews({ __new: true, title: '', summary: '', stage: 'final_published', source: 'manual', gazette_number: '', subject: '', source_url: '', full_text: '' })} className="rounded-none" data-testid="add-news-btn">
                <Plus size={14} strokeWidth={1.5} /> New update
              </Button>
            </div>
            <div className="border border-border scroll-table">
              <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                <div className="col-span-4">Title</div>
                <div className="col-span-2">Stage</div>
                <div className="col-span-2">Source</div>
                <div className="col-span-2">Crawled</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              {news.map((n) => (
                <div key={n.news_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center">
                  <div className="col-span-4">
                    <div className="font-medium line-clamp-1">{n.title}</div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-1 line-clamp-1">{n.summary}</div>
                  </div>
                  <div className="col-span-2 font-mono text-xs uppercase">{n.stage.replace(/_/g, ' ')}</div>
                  <div className="col-span-2 font-mono text-xs uppercase">{n.source}</div>
                  <div className="col-span-2 font-mono text-xs text-muted-foreground">{new Date(n.crawled_at).toLocaleDateString()}</div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button onClick={() => setEditingNews(n)} className="w-8 h-8 grid place-items-center border border-border hover:bg-surface-hover" data-testid={`edit-news-${n.news_id}`}>
                      <Edit3 size={13} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this news item?')) return;
                        try { await api.delete(`/admin/news/${n.news_id}`); toast.success('Deleted'); loadAll(); }
                        catch { toast.error('Could not delete'); }
                      }}
                      className="w-8 h-8 grid place-items-center border border-border hover:bg-severity-high hover:text-background"
                      data-testid={`delete-news-${n.news_id}`}
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* INGEST */}
          <TabsContent value="ingest" className="mt-6">
            <div className="panel p-8 max-w-3xl">
              <div className="label-mono mb-2">Ingest a regulation page</div>
              <h2 className="text-xl font-medium mb-1">Paste a URL — we extract title + content and save it as a manual news item.</h2>
              <p className="text-sm text-muted-foreground mb-6">Works best on static FSSAI / Legal-Metrology / gazette pages. Saved items appear immediately in the Updates feed.</p>
              <div className="flex gap-2">
                <Input
                  value={ingestUrl}
                  onChange={(e) => setIngestUrl(e.target.value)}
                  placeholder="https://www.fssai.gov.in/notifications/…"
                  className="rounded-none flex-1 h-11"
                  data-testid="ingest-url-input"
                />
                <Button onClick={ingest} className="rounded-none h-11" data-testid="ingest-url-btn">
                  <Globe size={14} strokeWidth={1.5} /> Ingest
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* CRAWLS */}
          <TabsContent value="crawls" className="mt-6" data-testid="crawls-tab">
            <CrawlsPanel runs={legacyAnalytics.crawler_runs || []} onRefresh={loadAll} onCrawl={triggerCrawl} />
          </TabsContent>


          {/* AUDITS */}
          <TabsContent value="audits" className="mt-6">
            <div className="border border-border scroll-table">
              <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                <div className="col-span-3">File</div>
                <div className="col-span-2">User</div>
                <div className="col-span-2">Format</div>
                <div className="col-span-1">Score</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1 text-right">Date</div>
                <div className="col-span-1 text-right">Report</div>
              </div>
              {audits.map((a) => (
                <div key={a.audit_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center">
                  <div className="col-span-3 truncate">{a.file_name}</div>
                  <div className="col-span-2 font-mono text-xs truncate">{a.user_id || 'guest'}</div>
                  <div className="col-span-2 text-xs">{a.pack_config?.pack_format_id || '—'}</div>
                  <div className="col-span-1 font-mono">{a.overall_score || '—'}</div>
                  <div className="col-span-2 font-mono text-xs uppercase">{a.status}</div>
                  <div className="col-span-1 text-right font-mono text-xs">{new Date(a.created_at).toLocaleDateString()}</div>
                  <div className="col-span-1 text-right">
                    {a.status === 'complete' ? (
                      <button onClick={() => downloadPdf(a.audit_id)} className="w-8 h-8 inline-grid place-items-center border border-border hover:bg-surface-hover ml-auto" title="Download report" data-testid={`download-${a.audit_id}`}>
                        <Download size={13} strokeWidth={1.5} />
                      </button>
                    ) : (
                      <FileX2 size={13} className="text-muted-foreground inline" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-6">
            <div className="border border-border scroll-table">
              <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Name</div>
                <div className="col-span-1">Role</div>
                <div className="col-span-1">Plan</div>
                <div className="col-span-1">Audits</div>
                <div className="col-span-2">Credits</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              {users.map((u) => {
                const activeCr = computeActiveCredits(u.credits);
                const hasSub = !!u.razorpay_subscription_id;
                return (
                <div key={u.user_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center" data-testid={`admin-user-row-${u.user_id}`}>
                  <div className="col-span-3 truncate">{u.email}</div>
                  <div className="col-span-2 truncate">{u.name}</div>
                  <div className="col-span-1 font-mono uppercase text-xs">{u.role}</div>
                  <div className="col-span-1 font-mono uppercase text-xs">{u.plan}</div>
                  <div className="col-span-1 font-mono">{u.audits_total ?? 0}</div>
                  <div className="col-span-2 flex items-center gap-1.5">
                    <span className={`font-mono text-base ${activeCr > 0 ? 'text-gradient-pt' : 'text-muted-foreground'}`} data-testid={`admin-user-credits-${u.user_id}`}>{activeCr}</span>
                    {hasSub && (
                      <span className="font-mono text-[9px] uppercase tracking-widest border border-teal-500 text-teal-500 px-1.5 py-0.5" title="Active Razorpay subscription" data-testid={`admin-user-sub-${u.user_id}`}>SUB</span>
                    )}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button onClick={() => setBillingUser(u)} title="View billing" className="w-8 h-8 grid place-items-center border border-border hover:bg-surface-hover" data-testid={`billing-user-${u.user_id}`}>
                      <CreditCard size={13} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => setEditingUser(u)} className="w-8 h-8 grid place-items-center border border-border hover:bg-surface-hover" data-testid={`edit-user-${u.user_id}`}>
                      <Edit3 size={13} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Permanently delete ${u.email}? Their audits & sessions will also be removed.`)) return;
                        try { await api.delete(`/admin/users/${u.user_id}`); toast.success('User deleted'); loadAll(); }
                        catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
                      }}
                      className="w-8 h-8 grid place-items-center border border-border hover:bg-severity-high hover:text-background"
                      data-testid={`delete-user-${u.user_id}`}
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </TabsContent>

          {/* EMAIL — Gmail OAuth connect (admin's own + team roster) */}
          <TabsContent value="email" className="mt-6 space-y-6">
            <div className="panel p-8 max-w-3xl" data-testid="gmail-section">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="label-mono flex items-center gap-2"><Mail size={12} /> Your Gmail</div>
                  <h2 className="text-xl font-medium mt-1">Connect your account</h2>
                  <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                    Each designer (and you) connects their own Gmail. Audit reports are sent from the user
                    who clicks send — replies land in their inbox.
                  </p>
                </div>
                {gmail.connected ? (
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-gradient-pt px-2 py-1" data-testid="gmail-connected-pill">
                    <CheckCircle2 size={11} strokeWidth={1.8} /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-border px-2 py-1" data-testid="gmail-disconnected-pill">
                    Not connected
                  </span>
                )}
              </div>

              {!gmail.configured && (
                <div className="border border-severity-high p-4 text-sm text-severity-high font-mono mb-6">
                  Google OAuth is not configured on the server. Set GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET in backend/.env.
                </div>
              )}

              {gmail.connected ? (
                <>
                  <div className="border border-border bg-surface p-5 mb-6" data-testid="gmail-status-card">
                    <div className="grid grid-cols-3 gap-4 items-center">
                      <div>
                        <div className="label-mono mb-1">Sender</div>
                        <div className="text-sm">{gmail.sender_email}</div>
                      </div>
                      <div>
                        <div className="label-mono mb-1">Connected</div>
                        <div className="text-sm">{new Date(gmail.granted_at).toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <Button onClick={disconnectGmail} variant="outline" className="rounded-none" data-testid="gmail-disconnect-btn">
                          <Unplug size={14} strokeWidth={1.5} /> Disconnect
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-border pt-6">
                    <div className="label-mono mb-2">Send a test email</div>
                    <div className="flex gap-2">
                      <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="to@example.com" type="email" className="rounded-none flex-1 h-11" data-testid="gmail-test-email" />
                      <Button onClick={sendTestEmail} className="rounded-none h-11" data-testid="gmail-test-send">
                        <Send size={14} strokeWidth={1.5} /> Send test
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <Button onClick={connectGmail} disabled={!gmail.configured} className="rounded-none h-11" data-testid="gmail-connect-btn">
                  <Mail size={14} strokeWidth={1.5} /> Connect Gmail
                </Button>
              )}
            </div>

            {/* Team connections roster */}
            <div className="border border-border scroll-table" data-testid="gmail-connections-table">
              <div className="flex items-center justify-between px-4 py-3 bg-surface">
                <div className="label-mono">Team connections ({gmailConnections.length})</div>
                <div className="text-xs text-muted-foreground">Every PackCheck user who has connected their Gmail.</div>
              </div>
              <div className="grid grid-cols-12 px-4 py-3 border-t border-border label-mono">
                <div className="col-span-4">User</div>
                <div className="col-span-3">Sender Gmail</div>
                <div className="col-span-2">Role</div>
                <div className="col-span-3 text-right">Connected</div>
              </div>
              {gmailConnections.length === 0 && (
                <div className="p-6 text-sm text-muted-foreground text-center">No team connections yet. Designers can connect their own Gmail under <span className="font-mono">Account → Email delivery</span>.</div>
              )}
              {gmailConnections.map((c) => (
                <div key={c.user_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center">
                  <div className="col-span-4">
                    <div className="font-medium">{c.user_name || '—'}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{c.user_email}</div>
                  </div>
                  <div className="col-span-3 text-sm">{c.sender_email}</div>
                  <div className="col-span-2 font-mono text-xs uppercase">{c.role}</div>
                  <div className="col-span-3 text-right font-mono text-xs text-muted-foreground">{new Date(c.granted_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* EMAIL LOG */}
          <TabsContent value="emails" className="mt-6">
            <div className="border border-border scroll-table">
              <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                <div className="col-span-3">To</div>
                <div className="col-span-3">Subject</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Sent</div>
              </div>
              {emails.length === 0 && <div className="p-6 text-sm text-muted-foreground">No emails sent yet. {legacyAnalytics.gmail_ready ? 'Gmail integration is ready.' : 'Configure GMAIL_CLIENT_ID, GMAIL_CLIENT_SECRET, GMAIL_REFRESH_TOKEN, GMAIL_SENDER in backend/.env to activate live sending.'}</div>}
              {emails.map((e) => (
                <div key={e.log_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm">
                  <div className="col-span-3">{e.to_email}</div>
                  <div className="col-span-3 truncate">{e.subject}</div>
                  <div className="col-span-2 font-mono text-xs uppercase">{e.type}</div>
                  <div className="col-span-2 font-mono text-xs uppercase">{e.status}</div>
                  <div className="col-span-2 text-right font-mono text-xs">{new Date(e.sent_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* RULE EDITOR MODAL */}
      <RuleEditorModal rule={editingRule} onClose={() => setEditingRule(null)} onSaved={loadAll} />
      {/* NEWS EDITOR MODAL */}
      <NewsEditorModal news={editingNews} onClose={() => setEditingNews(null)} onSaved={loadAll} />
      {/* USER EDITOR MODAL */}
      <UserEditorModal user={editingUser} onClose={() => setEditingUser(null)} onSaved={loadAll} />
      {/* USER BILLING MODAL */}
      <UserBillingModal user={billingUser} onClose={() => setBillingUser(null)} />
    </Layout>
  );
}

// ---------- modals ----------

const RuleEditorModal = ({ rule, onClose, onSaved }) => {
  const isNew = !!rule?.__new;
  const [form, setForm] = useState(rule);
  useEffect(() => setForm(rule), [rule]);
  if (!rule || !form) return null;

  const save = async () => {
    try {
      const body = {
        check_id: form.check_id, category: form.category, check_type: form.check_type || 'text',
        what_to_detect: form.what_to_detect, severity: form.severity,
        red_flag_condition: form.red_flag_condition, improvement_suggestion: form.improvement_suggestion,
        regulation_reference: form.regulation_reference,
        applies_to_categories: form.applies_to_categories || [],
        requires_imported: !!form.requires_imported, requires_ecommerce: !!form.requires_ecommerce,
        requires_visual: !!form.requires_visual, version_date: form.version_date || '',
        is_active: form.is_active !== false,
      };
      if (isNew) await api.post('/regulations', body);
      else await api.put(`/regulations/${form.check_id}`, body);
      toast.success(isNew ? 'Rule created' : 'Rule updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  return (
    <Dialog open={!!rule} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New regulation rule' : `Edit rule · ${form.check_id}`}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto p-1">
          <div className={isNew ? '' : 'opacity-60'}>
            <Label className="label-mono-sm">Check ID</Label>
            <Input value={form.check_id || ''} disabled={!isNew} onChange={(e) => setForm({ ...form, check_id: e.target.value })} className="rounded-none mt-1" data-testid="rule-check-id" />
          </div>
          <div>
            <Label className="label-mono-sm">Severity</Label>
            <Select value={form.severity || 'MEDIUM'} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono-sm">Category</Label>
            <Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="label-mono-sm">Check type</Label>
            <Input value={form.check_type || ''} onChange={(e) => setForm({ ...form, check_type: e.target.value })} className="rounded-none mt-1" placeholder="text | visual | structural" />
          </div>
          <div className="col-span-2">
            <Label className="label-mono-sm">What to detect</Label>
            <textarea rows={2} value={form.what_to_detect || ''} onChange={(e) => setForm({ ...form, what_to_detect: e.target.value })} className="w-full rounded-none border border-input bg-background p-3 text-sm mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="label-mono-sm">Red-flag condition (what triggers the flag)</Label>
            <textarea rows={2} value={form.red_flag_condition || ''} onChange={(e) => setForm({ ...form, red_flag_condition: e.target.value })} className="w-full rounded-none border border-input bg-background p-3 text-sm mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="label-mono-sm">Improvement suggestion (shown to designer)</Label>
            <textarea rows={2} value={form.improvement_suggestion || ''} onChange={(e) => setForm({ ...form, improvement_suggestion: e.target.value })} className="w-full rounded-none border border-input bg-background p-3 text-sm mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="label-mono-sm">Regulation reference</Label>
            <Input value={form.regulation_reference || ''} onChange={(e) => setForm({ ...form, regulation_reference: e.target.value })} className="rounded-none mt-1" placeholder="FSSAI Reg 2.x.x / LMPC 2011" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} className="rounded-none" data-testid="save-rule-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NewsEditorModal = ({ news, onClose, onSaved }) => {
  const isNew = !!news?.__new;
  const [form, setForm] = useState(news);
  useEffect(() => setForm(news), [news]);
  if (!news || !form) return null;

  const save = async () => {
    try {
      const body = {
        title: form.title, summary: form.summary || '', stage: form.stage || 'final_published',
        source: form.source || 'manual', gazette_number: form.gazette_number || '',
        subject: form.subject || '', source_url: form.source_url || '', full_text: form.full_text || '',
      };
      if (isNew) await api.post('/admin/news', body);
      else await api.put(`/admin/news/${form.news_id}`, body);
      toast.success(isNew ? 'News added' : 'News updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  return (
    <Dialog open={!!news} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add news update' : `Edit news`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1">
          <div>
            <Label className="label-mono-sm">Title</Label>
            <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" data-testid="news-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-mono-sm">Stage</Label>
              <Select value={form.stage || 'final_published'} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['draft_gazette', 'public_consultation', 'final_published', 'enforcement'].map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-mono-sm">Source</Label>
              <Select value={form.source || 'manual'} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['fssai', 'legal_metrology', 'manual'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-mono-sm">Gazette number</Label>
              <Input value={form.gazette_number || ''} onChange={(e) => setForm({ ...form, gazette_number: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="label-mono-sm">Subject</Label>
              <Input value={form.subject || ''} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none mt-1" />
            </div>
          </div>
          <div>
            <Label className="label-mono-sm">Source URL</Label>
            <Input value={form.source_url || ''} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="label-mono-sm">Summary</Label>
            <textarea rows={3} value={form.summary || ''} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="w-full rounded-none border border-input bg-background p-3 text-sm mt-1" />
          </div>
          <div>
            <Label className="label-mono-sm">Full text</Label>
            <textarea rows={5} value={form.full_text || ''} onChange={(e) => setForm({ ...form, full_text: e.target.value })} className="w-full rounded-none border border-input bg-background p-3 text-sm mt-1 font-mono" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} className="rounded-none" data-testid="save-news-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserEditorModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState(user);
  useEffect(() => setForm(user), [user]);
  if (!user || !form) return null;

  const save = async () => {
    try {
      await api.patch(`/admin/users/${form.user_id}`, {
        role: form.role, plan: form.plan, name: form.name,
      });
      toast.success('User updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle>Edit user · <span className="font-mono text-sm">{user.email}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-mono-sm">Name</Label>
            <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-mono-sm">Role</Label>
              <Select value={form.role || 'user'} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['user', 'admin'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-mono-sm">Plan</Label>
              <Select value={form.plan || 'free'} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="user-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['free', 'pro', 'admin'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} className="rounded-none" data-testid="save-user-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserBillingModal = ({ user, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!user) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/users/${user.user_id}/billing`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load billing'))
      .finally(() => setLoading(false));
  }, [user]);
  if (!user) return null;
  const allBundles = data?.all_bundles || [];
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="user-billing-modal">
        <DialogHeader>
          <DialogTitle>Billing · <span className="font-mono text-sm">{user.email}</span></DialogTitle>
        </DialogHeader>
        {loading && <div className="dot-loader my-6"><span/><span/><span/></div>}
        {data && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-border bg-surface p-4">
                <div className="label-mono mb-1">Active credits</div>
                <div className="font-mono text-3xl text-gradient-pt" data-testid="billing-total-active">{data.total_active}</div>
              </div>
              <div className="border border-border bg-surface p-4">
                <div className="label-mono mb-1">Subscription</div>
                <div className="text-sm">{data.razorpay_subscription_id ? 'Active' : '—'}</div>
                {data.razorpay_subscription_id && (
                  <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mt-1 truncate">{data.razorpay_subscription_id}</div>
                )}
              </div>
              <div className="border border-border bg-surface p-4">
                <div className="label-mono mb-1">Account type</div>
                <div className="text-sm">{data.internal ? 'Internal (Therefore)' : 'External'}</div>
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">All credit bundles ({allBundles.length})</div>
              <div className="border border-border scroll-table">
                <div className="grid grid-cols-12 px-3 py-2 bg-surface label-mono">
                  <div className="col-span-3">Type</div>
                  <div className="col-span-1">Remain</div>
                  <div className="col-span-1">Granted</div>
                  <div className="col-span-2">Source</div>
                  <div className="col-span-2">Purchased</div>
                  <div className="col-span-3">Expires</div>
                </div>
                {allBundles.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No bundles.</div>}
                {allBundles.map((b) => (
                  <div key={b.bundle_id} className="grid grid-cols-12 px-3 py-2 border-t border-border text-xs items-center">
                    <div className="col-span-3 capitalize">{(b.type || '').replace(/_/g, ' ')}</div>
                    <div className="col-span-1 font-mono">{b.remaining}</div>
                    <div className="col-span-1 font-mono">{b.granted}</div>
                    <div className="col-span-2 font-mono truncate" title={b.source}>{b.source || '—'}</div>
                    <div className="col-span-2 font-mono">{b.purchased_at ? new Date(b.purchased_at).toLocaleDateString() : '—'}</div>
                    <div className="col-span-3 font-mono">{b.expires_at ? new Date(b.expires_at).toLocaleDateString() : 'Never'}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">Payments ({data.payments.length})</div>
              <div className="border border-border scroll-table">
                <div className="grid grid-cols-12 px-3 py-2 bg-surface label-mono">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-2">Plan</div>
                  <div className="col-span-2">Amount</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-3">Reference</div>
                </div>
                {data.payments.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No payments yet.</div>}
                {data.payments.map((p) => (
                  <div key={p.payment_id} className="grid grid-cols-12 px-3 py-2 border-t border-border text-xs items-center">
                    <div className="col-span-3 font-mono">{new Date(p.created_at).toLocaleString()}</div>
                    <div className="col-span-2 font-mono uppercase">{p.plan_id}</div>
                    <div className="col-span-2 font-mono">₹{(p.amount_inr || 0).toLocaleString('en-IN')}</div>
                    <div className="col-span-2 font-mono uppercase">{p.status}</div>
                    <div className="col-span-3 font-mono text-muted-foreground truncate" title={p.order_id || p.subscription_id || ''}>{p.order_id || p.subscription_id || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const CouponsTab = () => {
  const [coupons, setCoupons] = React.useState([]);
  const [form, setForm] = React.useState({ code: '', kind: 'pct', discount_pct: 10, discount_inr: 100, usage_limit_per_user: 1, total_usage_limit: '', valid_until: '', is_active: true });
  const load = React.useCallback(async () => {
    try { const { data } = await api.get('/admin/coupons'); setCoupons(data.items || []); } catch { /* */ }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.code) { toast.error('Coupon code required'); return; }
    const body = {
      code: form.code.toUpperCase(),
      usage_limit_per_user: Number(form.usage_limit_per_user) || 1,
      total_usage_limit: form.total_usage_limit ? Number(form.total_usage_limit) : null,
      valid_until: form.valid_until || null,
      is_active: form.is_active,
    };
    if (form.kind === 'pct') body.discount_pct = Number(form.discount_pct);
    else body.discount_inr = Number(form.discount_inr);
    try { await api.post('/admin/coupons', body); toast.success(`Coupon ${body.code} created`); setForm({ ...form, code: '' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create'); }
  };
  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try { await api.delete(`/admin/coupons/${code}`); toast.success(`Coupon ${code} deleted`); load(); }
    catch { toast.error('Could not delete'); }
  };
  return (
    <div className="space-y-6">
      <div className="panel p-6 max-w-3xl" data-testid="coupon-form">
        <div className="label-mono mb-3">Create coupon</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH20" className="rounded-none h-10" data-testid="coupon-code-input" />
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="h-10 border border-input bg-background px-2 rounded-none">
            <option value="pct">% off</option>
            <option value="inr">₹ off</option>
          </select>
          {form.kind === 'pct'
            ? <Input type="number" min={1} max={100} value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="rounded-none h-10" placeholder="20" />
            : <Input type="number" min={1} value={form.discount_inr} onChange={(e) => setForm({ ...form, discount_inr: e.target.value })} className="rounded-none h-10" placeholder="100" />}
          <Input type="number" min={1} value={form.usage_limit_per_user} onChange={(e) => setForm({ ...form, usage_limit_per_user: e.target.value })} className="rounded-none h-10" placeholder="Per-user uses (1)" />
          <Input type="number" min={1} value={form.total_usage_limit} onChange={(e) => setForm({ ...form, total_usage_limit: e.target.value })} className="rounded-none h-10" placeholder="Total uses (unlimited)" />
          <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="rounded-none h-10" />
        </div>
        <Button onClick={create} className="rounded-none mt-4" data-testid="coupon-create-btn">Create coupon</Button>
      </div>
      <div className="border border-border scroll-table">
        <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
          <div className="col-span-2">Code</div>
          <div className="col-span-2">Discount</div>
          <div className="col-span-2">Per-user limit</div>
          <div className="col-span-2">Total used</div>
          <div className="col-span-2">Expires</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {coupons.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No coupons yet.</div>}
        {coupons.map((c) => (
          <div key={c.code} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center">
            <div className="col-span-2 font-mono">{c.code}</div>
            <div className="col-span-2 font-mono text-xs">{c.discount_pct ? `${c.discount_pct}%` : `₹${c.discount_inr}`}</div>
            <div className="col-span-2 font-mono text-xs">{c.usage_limit_per_user}</div>
            <div className="col-span-2 font-mono text-xs">{c.redeemed_count || 0}{c.total_usage_limit ? ` / ${c.total_usage_limit}` : ''}</div>
            <div className="col-span-2 font-mono text-xs">{c.valid_until ? new Date(c.valid_until).toLocaleDateString() : '—'}</div>
            <div className="col-span-2 text-right">
              <button onClick={() => del(c.code)} className="w-8 h-8 inline-grid place-items-center border border-border hover:bg-severity-high hover:text-background" data-testid={`coupon-delete-${c.code}`}>
                <Trash2 size={13} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ================================================================
// Crawler runs panel
// ================================================================

const STATUS_COLOR = {
  success:    'bg-teal-500 text-white',
  degraded:   'bg-amber-500 text-black',
  failed:     'bg-red-600 text-white',
  error:      'bg-red-600 text-white',
  zero_items: 'bg-neutral-500 text-white',
};

const StatusPill = ({ status }) => (
  <span
    className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 ${STATUS_COLOR[status] || 'bg-neutral-500 text-white'}`}
    data-testid={`status-pill-${status}`}
  >
    {status || 'unknown'}
  </span>
);

const CrawlsPanel = ({ runs, onRefresh, onCrawl }) => {
  const [expanded, setExpanded] = useState(runs && runs.length ? 0 : -1);
  const totalOk = runs.filter(r => r.status === 'success').length;
  const totalDeg = runs.filter(r => r.status === 'degraded').length;
  const totalErr = runs.filter(r => r.status === 'failed' || r.status === 'error').length;

  return (
    <div data-testid="crawls-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-mono">Crawler runs · last {runs.length}</div>
          <p className="text-sm text-muted-foreground mt-1">
            Scheduled Sunday 02:00 IST. Each run also emails a digest to all admins.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="text-teal-500">{totalOk} ok</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="text-amber-500">{totalDeg} degraded</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="text-red-500">{totalErr} failed</span>
          </span>
          <Button onClick={onRefresh} variant="outline" className="rounded-none" data-testid="refresh-crawls-btn">
            <RefreshCw size={13} strokeWidth={1.5} /> Refresh
          </Button>
          <Button onClick={onCrawl} className="rounded-none" data-testid="crawls-run-now-btn">
            <RefreshCw size={13} strokeWidth={1.5} /> Run now
          </Button>
        </div>
      </div>

      {runs.length === 0 && (
        <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="no-crawls">
          No crawl runs yet. Click <b>Run now</b> to trigger one.
        </div>
      )}

      <div className="space-y-2">
        {runs.map((r, i) => {
          const isOpen = expanded === i;
          const per = Array.isArray(r.per_source) ? r.per_source : [];
          const perOk = per.filter(s => s.status === 'success').length;
          return (
            <div key={r.log_id || i} className="border border-border" data-testid={`crawl-row-${i}`}>
              <button
                onClick={() => setExpanded(isOpen ? -1 : i)}
                className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-surface-hover transition-colors"
              >
                <div className="font-mono text-xs w-40 shrink-0" data-testid={`crawl-when-${i}`}>
                  {r.run_at ? new Date(r.run_at).toLocaleString() : '—'}
                </div>
                <StatusPill status={r.status} />
                <div className="text-sm font-mono">
                  {r.new_items_found ?? 0} new · {perOk}/{per.length} sources OK
                </div>
                {r.error_text && (
                  <div className="text-xs text-red-500 font-mono truncate flex-1" title={r.error_text}>
                    {r.error_text}
                  </div>
                )}
                <div className="ml-auto text-xs font-mono text-muted-foreground">
                  {isOpen ? '−' : '+'}
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-border">
                  {per.length === 0 && (
                    <div className="px-4 py-4 text-xs text-muted-foreground">
                      This older run has no per-source detail — only newer runs record it.
                    </div>
                  )}
                  {per.map((s, si) => (
                    <div key={si} className="px-4 py-3 border-t border-border first:border-t-0 grid grid-cols-12 gap-3 items-center text-sm">
                      <div className="col-span-4">
                        <div className="font-medium">{s.subject || s.source_id}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                          {s.source_id}
                        </div>
                      </div>
                      <div className="col-span-2"><StatusPill status={s.status} /></div>
                      <div className="col-span-1 font-mono text-sm text-right">{s.items_fetched ?? 0}</div>
                      <div className="col-span-5 font-mono text-[10px] text-muted-foreground truncate" title={s.url}>
                        {s.error ? <span className="text-red-500">{s.error}</span> : (s.url || '')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
